import { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { useHistory } from 'react-router-dom';
import { Dialog } from '@mui/material';

import { TileCard } from '../Tiles/TileCard.jsx';
import { ScrollArrow } from '../Shared/ScrollArrow.jsx';
import { useFetchShelfTiles } from '../../hooks/useFetchShelfTiles';
import { ShelfHeader } from './ShelfHeader.jsx';
import { ButtonBase } from '../../../../shared/Button';
import { setScrollPosition as setCampaignScrollPos } from '../../../../lib/redux/campaignScroll/campaignScrollSlice';
import { ManageAccess } from '../../../../shared/ManageAccess/ManageAccess.jsx';

export const Shelf = ({ campaignId }) => {
  useFetchShelfTiles({ forceRefetch: true });
  const tiles = useSelector(state => state.campaignDnd);
  const [selectedTab, setSelectedTab] = useState('unscheduled');
  const [openManageAccess, setOpenManageAccess] = useState(false);

  const overFlowRef = useRef();

  const { locked } = useSelector(state => state.navBarCollapse);
  const [scrollPos, setScrollPos] = useState(0);
  const { t } = useTranslation('campaigns');
  const unscheduledSelected = selectedTab === 'unscheduled';
  const suggestedSelected = selectedTab === 'suggested';
  const { setNodeRef, isOver } = useDroppable({ id: selectedTab });
  const history = useHistory();
  const dispatch = useDispatch();

  const calculateNewScrollPos = () => {
    const { scrollHeight, clientHeight } = overFlowRef.current;
    const maxScrollHeight = scrollHeight - clientHeight;

    // when using the scrollbar, it is always short by 1 pixel when compared
    // to the maxScrollWidth
    const nextScrollPos = Math.ceil(overFlowRef.current.scrollTop + 1);

    if (nextScrollPos <= 1) return 0;
    if (maxScrollHeight - nextScrollPos <= 0) return maxScrollHeight;

    return nextScrollPos;
  };

  const hasTiles = tiles[selectedTab].length > 0;

  return (
    <Container navBarLocked={locked}>
      <ShelfHeader
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        tiles={tiles}
      />
      {unscheduledSelected && (
        <ShelfButton
          as="div"
          id="shelf-button"
          mode="outline"
          variant="primary"
          onClick={() => {
            dispatch(
              setCampaignScrollPos({
                page: 'edit',
                scrollPosition: window.scrollY,
              }),
            );
            const isInPlanPage = window.location.pathname.includes('/plan');
            const prefix = isInPlanPage ? '/campaigns' : '';
            history.push(`${prefix}/${campaignId}/tile/new`);
          }}
        >
          <AddTileIcon />
          {t('shelf.addTile')}
        </ShelfButton>
      )}
      {suggestedSelected && (
        <>
          <ShelfButton
            onClick={() => setOpenManageAccess(true)}
            as="div"
            mode="outline"
            variant="primary"
          >
            Manage Access
          </ShelfButton>
          <Dialog open={openManageAccess}>
            <ManageAccess onClose={() => setOpenManageAccess(false)} />
          </Dialog>
        </>
      )}
      {hasTiles && (
        <ScrollArrow
          direction="up"
          scrollContainer={overFlowRef.current || {}}
          scrollPos={scrollPos}
          setScrollPos={setScrollPos}
        />
      )}
      <Tiles
        className="tiles-list"
        ref={el => {
          setNodeRef(el);
          overFlowRef.current = el;
        }}
        isOver={isOver}
        unscheduled={unscheduledSelected}
        onScroll={() => setScrollPos(calculateNewScrollPos)}
      >
        <SortableContext items={tiles[selectedTab]}>
          {tiles[selectedTab].map(tile => (
            <TileCard key={tile.id} tile={tile} />
          ))}
        </SortableContext>
      </Tiles>
      {hasTiles && (
        <ScrollArrow
          direction="down"
          scrollContainer={overFlowRef.current || {}}
          scrollPos={scrollPos}
          setScrollPos={setScrollPos}
        />
      )}
    </Container>
  );
};

const ShelfButton = styled(ButtonBase)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 190px;
  border-radius: 8px;
  cursor: pointer;
  margin: 8px 0;
  align-self: center;
  border-color: ${themeGet('colors.black.10')};
`;

const Tiles = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
  padding: 12px 0;
  height: 100%;
  box-shadow: ${({ isOver }) => (isOver ? 'inset 0 0 10px #99CBFF' : 'none')};
  transition: box-shadow 0.5s;
  overflow-y: scroll;
  position: relative;
  align-items: center;
  max-height: calc(85vh - 64px);

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgba(196, 196, 196, 0.24);
  }
`;

const Container = styled.div`
  transition: height ease-in 0.3s;
  transition: width 0.3s ease-in-out;
  background: ${themeGet('colors.white')};
  border: 1px solid ${themeGet('colors.black.10')};
  width: 260px;
  min-width: 260px;
  min-height: calc(100vh - 118px);
  max-height: calc(100vh - 118px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: sticky;
  top: 75px;
`;

const AddTileIcon = styled.i.attrs({
  className: 'fa-solid fa-plus',
})`
  margin-right: 6px;
`;
